import type { Deliberation } from '@/types/deliberation'
import type { Filiere } from '@/types/administration'

export type DeliberationSessionCategory = 'en_cours' | 'prevue' | 'terminee'

/**
 * `session` : cycle complet (1ère obligatoire par semestre attendu) — vue admin / RP.
 * `assigned` : uniquement les délibs présentes dans le dataset (vue « Mes délibérations »).
 */
export type DeliberationCategoryScope = 'session' | 'assigned'

export interface DeliberationSessionMeta {
	annee: string
	niveauType: string
	niveauNumber: number
	filiereId: number | null
	filiereLabel: string
	classeLabel: string
}

export interface DeliberationSessionTreeNode {
	session: any
	category: DeliberationSessionCategory
	meta: DeliberationSessionMeta
}

export interface DeliberationFiliereGroup {
	key: string
	filiereId: number | null
	filiereLabel: string
	sessions: DeliberationSessionTreeNode[]
}

export interface DeliberationNiveauGroup {
	niveau: string
	filieres: DeliberationFiliereGroup[]
}

export interface DeliberationAnneeGroup {
	annee: string
	niveaux: DeliberationNiveauGroup[]
}

const NIVEAU_ORDER = ['Licence', 'Master', 'Certification', 'Autres']

const ETATS_CLOTURES = new Set(['valide', 'annule'])
const ETATS_ACTIFS = new Set(['en_cours', 'phase_1_terminee', 'phase_2_terminee'])

export function extractNiveauType(niveau: string | null | undefined): string {
	if (!niveau) return 'Autres'
	const niveauUpper = niveau.toString().trim().toUpperCase()
	if (niveauUpper.includes('LICENCE')) return 'Licence'
	if (niveauUpper.includes('MASTER')) return 'Master'
	if (niveauUpper.includes('CERTIFICATION')) return 'Certification'
	return 'Autres'
}

export function extractNiveauNumber(niveau: string | null | undefined): number {
	if (!niveau) return 0
	const match = niveau.toString().match(/(\d+)/)
	return match ? parseInt(match[1], 10) : 0
}

export function resolveSessionMeta(
	session: any,
	filieresById?: Map<number, Filiere> | Record<number, Filiere>
): DeliberationSessionMeta {
	const classe = session?.classe
	const annee = (session?.annee_universitaire || 'Année non renseignée').toString().trim() || 'Année non renseignée'
	const niveauRaw = classe?.niveau
	const niveauType = extractNiveauType(niveauRaw)
	const niveauNumber = extractNiveauNumber(niveauRaw)

	let filiere = classe?.filiere
	const filiereId: number | null = filiere?.id ?? classe?.filiere_id ?? null

	if (!filiere && filiereId != null && filieresById) {
		filiere = filieresById instanceof Map
			? filieresById.get(filiereId)
			: filieresById[filiereId]
	}

	const filiereLabel = (filiere?.nom || filiere?.code || 'Filière non renseignée').toString().trim()
	const classeLabel = (classe?.intitule || classe?.code || '').toString().trim()

	return {
		annee,
		niveauType,
		niveauNumber,
		filiereId,
		filiereLabel,
		classeLabel,
	}
}

function uniqueSortedNumbers(values: number[]): number[] {
	return Array.from(new Set(values)).sort((a, b) => a - b)
}

/** Semestres attendus : session.semestres si présents, sinon ceux déjà vus dans les délibs. */
export function getExpectedSemestreIds(session: any, delibs: Deliberation[]): number[] {
	const fromSession: number[] = Array.isArray(session?.semestres)
		? session.semestres
			.map((s: any) => s?.id)
			.filter((id: unknown): id is number => typeof id === 'number')
		: []

	if (fromSession.length > 0) {
		return Array.from(new Set(fromSession))
	}

	const fromDelibs: number[] = delibs
		.map(d => d.semestre?.id ?? d.semestre_id)
		.filter((id): id is number => typeof id === 'number')

	return Array.from(new Set(fromDelibs))
}

/** Numéros de semestre attendus via classe.semestre_possible (ex. "1,2"). */
export function getExpectedSemestreNumeros(session: any, delibs: Deliberation[]): number[] {
	const raw = session?.classe?.semestre_possible
	if (raw != null && String(raw).trim() !== '') {
		const nums: number[] = String(raw)
			.split(/[,;\s]+/)
			.map(s => parseInt(s.trim(), 10))
			.filter(n => !Number.isNaN(n) && n > 0)
		if (nums.length > 0) return uniqueSortedNumbers(nums)
	}

	if (Array.isArray(session?.semestres) && session.semestres.length > 0) {
		const nums: number[] = session.semestres
			.map((s: any) => s?.numero)
			.filter((n: unknown): n is number => typeof n === 'number')
		if (nums.length > 0) return uniqueSortedNumbers(nums)
	}

	const fromDelibs: number[] = delibs
		.map(d => d.semestre?.numero)
		.filter((n): n is number => typeof n === 'number')
	return uniqueSortedNumbers(fromDelibs)
}

function isSlotClos(etat?: string | null): boolean {
	return !!etat && ETATS_CLOTURES.has(etat)
}

/**
 * Cycle 1ère (+ 2nde si elle a lieu d'être) clos pour un slot spécialité × semestre.
 *
 * Règles :
 * - 1ère absente ou non close → pas clos
 * - 2nde existante → doit être close (elle n'existe que si créée pour un rattrapage)
 * - 2nde absente + flag API `seconde_requise` / `nombre_etudiants_rattrapage > 0` → pas clos
 * - 2nde absente sans signal de besoin → clos (pas d'étudiants en rattrapage ⇒ pas de 2nde à attendre)
 */
export function isSlotCycleClos(slotDelibs: Deliberation[]): boolean {
	const premiere = slotDelibs.find(d => d.type_deliberation === 'premiere')
	const seconde = slotDelibs.find(d => d.type_deliberation === 'seconde')

	if (!premiere || !isSlotClos(premiere.etat)) {
		return false
	}

	if (seconde) {
		return isSlotClos(seconde.etat)
	}

	if (premiere.seconde_requise === true) {
		return false
	}

	const nbRattrapage = premiere.nombre_etudiants_rattrapage
	if (typeof nbRattrapage === 'number' && nbRattrapage > 0) {
		return false
	}

	return true
}

/**
 * Session bouclée (scope `session`) : pour chaque spécialité engagée et chaque semestre attendu,
 * le cycle 1ère (+ 2nde seulement si elle a lieu d'être) est clos ; aucune délib active.
 *
 * Scope `assigned` : le dataset est déjà filtré (responsable) — on ne exige pas une 1ère
 * absente du périmètre. Terminé dès que toutes les délibs visibles sont closes.
 */
export function isSessionBouclee(
	session: any,
	delibs: Deliberation[],
	scope: DeliberationCategoryScope = 'session'
): boolean {
	if (!delibs.length) return false

	if (delibs.some(d => ETATS_ACTIFS.has(d.etat))) {
		return false
	}

	if (scope === 'assigned') {
		// Périmètre assigné : closes uniquement. Une 2nde n'apparaît ici que si elle existe
		// (donc a été créée pour un rattrapage) — pas d'exigence d'une 2nde absente.
		return delibs.every(d => isSlotClos(d.etat))
	}

	const specialiteIds = [...new Set(
		delibs
			.map(d => d.specialite?.id ?? d.specialite_id)
			.filter((id): id is number => typeof id === 'number')
	)]
	if (specialiteIds.length === 0) return false

	const expectedNumeros = getExpectedSemestreNumeros(session, delibs)
	const expectedSemestreIds = getExpectedSemestreIds(session, delibs)

	// Préférer le contrôle par numéro de semestre (plus fiable si session.semestres absent)
	if (expectedNumeros.length > 0) {
		for (const specialiteId of specialiteIds) {
			for (const numero of expectedNumeros) {
				const slotDelibs = delibs.filter(d => {
					const sid = d.specialite?.id ?? d.specialite_id
					const num = d.semestre?.numero
					return sid === specialiteId && num === numero
				})

				if (!isSlotCycleClos(slotDelibs)) {
					return false
				}
			}
		}
		return true
	}

	if (expectedSemestreIds.length === 0) return false

	for (const specialiteId of specialiteIds) {
		for (const semestreId of expectedSemestreIds) {
			const slotDelibs = delibs.filter(d => {
				const sid = d.specialite?.id ?? d.specialite_id
				const semId = d.semestre?.id ?? d.semestre_id
				return sid === specialiteId && semId === semestreId
			})

			if (!isSlotCycleClos(slotDelibs)) {
				return false
			}
		}
	}

	return true
}

export function getSessionCategory(
	session: any,
	delibs: Deliberation[],
	scope: DeliberationCategoryScope = 'session'
): DeliberationSessionCategory {
	if (!delibs.length) return 'prevue'

	if (isSessionBouclee(session, delibs, scope)) {
		return 'terminee'
	}

	// Session incomplète (ex. S1 validé, S2 pas encore ouvert) = encore en cours,
	// pas « prévue » — ce libellé n'a de sens que s'il n'y a aucun travail engagé.
	const hasEngagedWork = delibs.some(d =>
		ETATS_ACTIFS.has(d.etat) ||
		d.etat === 'valide' ||
		!!d.phase_1_date_debut
	)

	return hasEngagedWork ? 'en_cours' : 'prevue'
}

export function getSessionCategoryLabel(category: DeliberationSessionCategory): string {
	switch (category) {
		case 'en_cours':
			return 'En cours'
		case 'prevue':
			return 'Non démarrée'
		case 'terminee':
			return 'Terminée'
		default:
			return category
	}
}

export function getSessionCategoryColor(category: DeliberationSessionCategory): string {
	switch (category) {
		case 'en_cours':
			return 'info'
		case 'prevue':
			return 'grey'
		case 'terminee':
			return 'success'
		default:
			return 'grey'
	}
}

function compareAnneeDesc(a: string, b: string): number {
	return b.localeCompare(a, 'fr', { sensitivity: 'base', numeric: true })
}

function compareNiveau(a: string, b: string): number {
	const ia = NIVEAU_ORDER.indexOf(a)
	const ib = NIVEAU_ORDER.indexOf(b)
	const oa = ia === -1 ? NIVEAU_ORDER.length : ia
	const ob = ib === -1 ? NIVEAU_ORDER.length : ib
	if (oa !== ob) return oa - ob
	return a.localeCompare(b, 'fr', { sensitivity: 'base' })
}

function buildFilieresById(
	filieres?: Filiere[] | Map<number, Filiere> | Record<number, Filiere>
): Map<number, Filiere> {
	if (!filieres) return new Map()
	if (filieres instanceof Map) return filieres
	if (Array.isArray(filieres)) {
		return new Map(filieres.map(f => [f.id, f]))
	}
	return new Map(Object.entries(filieres).map(([k, v]) => [Number(k), v]))
}

function collectSessionNodes(
	sessions: any[],
	delibsBySession: Record<number, Deliberation[]>,
	filieresById: Map<number, Filiere>,
	categoryFilter?: DeliberationSessionCategory[],
	scope: DeliberationCategoryScope = 'session'
): DeliberationSessionTreeNode[] {
	const allowed = categoryFilter ? new Set(categoryFilter) : null
	const nodes: DeliberationSessionTreeNode[] = []

	for (const session of sessions) {
		if (!session?.id) continue
		const delibs = delibsBySession[session.id] || []
		const category = getSessionCategory(session, delibs, scope)
		if (allowed && !allowed.has(category)) continue
		nodes.push({
			session,
			category,
			meta: resolveSessionMeta(session, filieresById),
		})
	}

	return nodes
}

function sortSessionNodes(nodes: DeliberationSessionTreeNode[]): DeliberationSessionTreeNode[] {
	return [...nodes].sort((a, b) => {
		if (a.meta.niveauNumber !== b.meta.niveauNumber) {
			return a.meta.niveauNumber - b.meta.niveauNumber
		}
		const nameA = a.session.nom || a.session.code || ''
		const nameB = b.session.nom || b.session.code || ''
		return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base', numeric: true })
	})
}

function groupNodesByFiliere(nodes: DeliberationSessionTreeNode[]): DeliberationFiliereGroup[] {
	const byFiliere = new Map<string, DeliberationSessionTreeNode[]>()

	for (const node of nodes) {
		const { filiereLabel, filiereId } = node.meta
		const key = filiereId != null ? `id:${filiereId}` : `label:${filiereLabel}`
		if (!byFiliere.has(key)) byFiliere.set(key, [])
		byFiliere.get(key)!.push(node)
	}

	return [...byFiliere.entries()]
		.map(([key, sessionNodes]) => {
			const sorted = sortSessionNodes(sessionNodes)
			const first = sorted[0]
			return {
				key,
				filiereId: first?.meta.filiereId ?? null,
				filiereLabel: first?.meta.filiereLabel ?? 'Filière non renseignée',
				sessions: sorted,
			}
		})
		.sort((a, b) => a.filiereLabel.localeCompare(b.filiereLabel, 'fr', { sensitivity: 'base' }))
}

/**
 * Arbre sans année : Niveau → Filière → Sessions (pour les sessions encore actives).
 */
export function buildDeliberationNiveauTree(
	sessions: any[],
	delibsBySession: Record<number, Deliberation[]>,
	filieres?: Filiere[] | Map<number, Filiere> | Record<number, Filiere>,
	categoryFilter?: DeliberationSessionCategory[],
	scope: DeliberationCategoryScope = 'session'
): DeliberationNiveauGroup[] {
	const filieresById = buildFilieresById(filieres)
	const nodes = collectSessionNodes(sessions, delibsBySession, filieresById, categoryFilter, scope)

	const byNiveau = new Map<string, DeliberationSessionTreeNode[]>()
	for (const node of nodes) {
		const niveau = node.meta.niveauType
		if (!byNiveau.has(niveau)) byNiveau.set(niveau, [])
		byNiveau.get(niveau)!.push(node)
	}

	return [...byNiveau.keys()]
		.sort(compareNiveau)
		.map(niveau => ({
			niveau,
			filieres: groupNodesByFiliere(byNiveau.get(niveau)!),
		}))
}

/**
 * Arbre archives : Année → Filière → Sessions (l'année ne sert qu'aux sessions terminées).
 */
export function buildDeliberationAnneeFiliereTree(
	sessions: any[],
	delibsBySession: Record<number, Deliberation[]>,
	filieres?: Filiere[] | Map<number, Filiere> | Record<number, Filiere>,
	categoryFilter?: DeliberationSessionCategory[],
	scope: DeliberationCategoryScope = 'session'
): Array<{ annee: string; filieres: DeliberationFiliereGroup[] }> {
	const filieresById = buildFilieresById(filieres)
	const nodes = collectSessionNodes(sessions, delibsBySession, filieresById, categoryFilter, scope)

	const byAnnee = new Map<string, DeliberationSessionTreeNode[]>()
	for (const node of nodes) {
		const annee = node.meta.annee
		if (!byAnnee.has(annee)) byAnnee.set(annee, [])
		byAnnee.get(annee)!.push(node)
	}

	return [...byAnnee.keys()]
		.sort(compareAnneeDesc)
		.map(annee => ({
			annee,
			filieres: groupNodesByFiliere(byAnnee.get(annee)!),
		}))
}

/**
 * Construit l'arbre Année → Niveau → Filière → Sessions.
 * `categoryFilter` limite aux catégories voulues (ex. actives vs terminées).
 */
export function buildDeliberationSessionTree(
	sessions: any[],
	delibsBySession: Record<number, Deliberation[]>,
	filieres?: Filiere[] | Map<number, Filiere> | Record<number, Filiere>,
	categoryFilter?: DeliberationSessionCategory[],
	scope: DeliberationCategoryScope = 'session'
): DeliberationAnneeGroup[] {
	const filieresById = buildFilieresById(filieres)
	const nodes = collectSessionNodes(sessions, delibsBySession, filieresById, categoryFilter, scope)

	const byAnnee = new Map<string, Map<string, DeliberationSessionTreeNode[]>>()

	for (const node of nodes) {
		const { annee, niveauType } = node.meta
		if (!byAnnee.has(annee)) byAnnee.set(annee, new Map())
		const byNiveau = byAnnee.get(annee)!
		if (!byNiveau.has(niveauType)) byNiveau.set(niveauType, [])
		byNiveau.get(niveauType)!.push(node)
	}

	const annees = [...byAnnee.keys()].sort(compareAnneeDesc)

	return annees.map(annee => {
		const byNiveau = byAnnee.get(annee)!
		const niveaux = [...byNiveau.keys()].sort(compareNiveau).map(niveau => ({
			niveau,
			filieres: groupNodesByFiliere(byNiveau.get(niveau)!),
		}))
		return { annee, niveaux }
	})
}

/** Compte le nombre de sessions dans un arbre Année → Niveau → Filière. */
export function countSessionsInTree(tree: DeliberationAnneeGroup[]): number {
	return tree.reduce(
		(sum, annee) =>
			sum +
			annee.niveaux.reduce(
				(s2, niv) => s2 + niv.filieres.reduce((s3, f) => s3 + f.sessions.length, 0),
				0
			),
		0
	)
}

export function countSessionsInNiveauTree(tree: DeliberationNiveauGroup[]): number {
	return tree.reduce(
		(sum, niv) => sum + niv.filieres.reduce((s, f) => s + f.sessions.length, 0),
		0
	)
}

export function countSessionsInAnneeFiliereTree(
	tree: Array<{ annee: string; filieres: DeliberationFiliereGroup[] }>
): number {
	return tree.reduce(
		(sum, annee) => sum + annee.filieres.reduce((s, f) => s + f.sessions.length, 0),
		0
	)
}

/** Enrichit une session embarquée avec classe/semestres depuis une session API complète. */
export function mergeSessionRelations(embedded: any, full: any | undefined): any {
	if (!full) return embedded
	return {
		...embedded,
		...full,
		classe: full.classe ?? embedded.classe,
		semestres: full.semestres ?? embedded.semestres,
		annee_universitaire: full.annee_universitaire ?? embedded.annee_universitaire,
	}
}
